import { ApiRequest } from "GlobalFunctions";
import { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import {renderCartProducts} from "components/Orders/getOrderDetails";
// Validation and formatting functions from Subscription component
export const formatCardNumber = (value: string): string => {
    return value
        .replace(/\D/g, '')
        .replace(/(.{4})/g, '$1 ')
        .trim()
        .substring(0, 19);
};

export const formatExpirationDate = (value: string): string => {
    let cleanedValue = value.replace(/\D/g, '');
    if (cleanedValue.length > 2) {
        cleanedValue = cleanedValue.substring(0, 2) + '/' + cleanedValue.substring(2);
    }
    return cleanedValue.substring(0, 5);
};

export const validateExpirationDate = (value: string): boolean => {
    const parts = value.split('/');
    if (parts.length !== 2) return false;
    
    const month = parseInt(parts[0], 10);
    const year = parseInt(parts[1], 10);
    
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear() % 100;
    const currentMonth = currentDate.getMonth() + 1;
    
    if (month < 1 || month > 12) return false;
    if (year < currentYear) return false;
    if (year === currentYear && month < currentMonth) return false;
    
    return true;
};

export const validateCardNumber = (cardNumber: string): boolean => {
    // Check if the card number has the correct length (16 digits + 3 spaces = 19 characters)
    return cardNumber.length >= 19;
};
export default function SubscriptionUpdateCard(props:any) {

    const { subscriptionCode, ShowLoader } = props;
    const navigate = useNavigate();
    const [subscriptionData, setSubscriptionData] = useState<any>({});
    const [isUpdateSuccessful, setIsUpdateSuccessful] = useState(false);
    
    // Validation states
    const [validationState, setValidationState] = useState({
        isValidCardName: false,
        isValidCardNumber: false,
        isValidExpiration: false,
        isValidCVV: false
    });

    useEffect(() => {
        ShowLoader(false)
        getSubscriptionData();
        // Attach event listeners after component mounts
        setTimeout(() => {
            attachEventListeners();
        }, 100);
        
        // Cleanup function to remove event listeners
        return () => {
            removeEventListeners();
        };
    }, []);

    // Event listeners for card input formatting and validation
    const attachEventListeners = () => {
        const cardNameInput = document.querySelector('[pc-sub="card-name"]') as HTMLInputElement;
        const cardNumberInput = document.querySelector('[pc-sub="card-number"]') as HTMLInputElement;
        const cardExpirationInput = document.querySelector('[pc-sub="card-expiration"]') as HTMLInputElement;
        const cardCvvInput = document.querySelector('[pc-sub="card-cvv"]') as HTMLInputElement;

        if (cardNameInput) {
            cardNameInput.addEventListener('input', handleCardNameChange);
            cardNameInput.addEventListener('blur', handleCardNameChange);
        }

        if (cardNumberInput) {
            cardNumberInput.addEventListener('input', handleCardNumberChange);
            cardNumberInput.addEventListener('blur', handleCardNumberChange);
        }

        if (cardExpirationInput) {
            cardExpirationInput.addEventListener('input', handleCardExpirationChange);
            cardExpirationInput.addEventListener('blur', handleCardExpirationChange);
        }

        if (cardCvvInput) {
            cardCvvInput.addEventListener('input', handleCardCvvChange);
            cardCvvInput.addEventListener('blur', handleCardCvvChange);
        }
    };

    // Handler functions for validation and formatting
    const handleCardNameChange = (event: Event) => {
        const input = event.target as HTMLInputElement;
        const value = input.value.trim();
        const isValid = value.length >= 2;
        
        setValidationState(prev => ({ ...prev, isValidCardName: isValid }));
        
        if (isValid) {
            input.classList.remove('error');
        } else if (event.type === 'blur') {
            input.classList.add('error');
        }
    };

    const handleCardNumberChange = (event: Event) => {
        const input = event.target as HTMLInputElement;
        const formattedValue = formatCardNumber(input.value);
        
        if (formattedValue !== input.value) {
            input.value = formattedValue;
        }
        
        const isValid = validateCardNumber(formattedValue);
        setValidationState(prev => ({ ...prev, isValidCardNumber: isValid }));
        
        if (isValid) {
            input.classList.remove('error');
        } else if (event.type === 'blur') {
            input.classList.add('error');
        }
    };

    const handleCardExpirationChange = (event: Event) => {
        const input = event.target as HTMLInputElement;
        const formattedValue = formatExpirationDate(input.value);
        
        if (formattedValue !== input.value) {
            input.value = formattedValue;
        }
        
        const isValid = validateExpirationDate(formattedValue);
        setValidationState(prev => ({ ...prev, isValidExpiration: isValid }));
        
        if (isValid) {
            input.classList.remove('error');
        } else if (event.type === 'blur') {
            input.classList.add('error');
        }
    };

    const handleCardCvvChange = (event: Event) => {
        const input = event.target as HTMLInputElement;
        let value = input.value.replace(/\D/g, '');
        
        // Limit CVV to 4 digits
        if (value.length > 4) {
            value = value.substring(0, 4);
        }
        
        if (value !== input.value) {
            input.value = value;
        }
        
        const isValid = value.length >= 3;
        setValidationState(prev => ({ ...prev, isValidCVV: isValid }));
        
        if (isValid) {
            input.classList.remove('error');
        } else if (event.type === 'blur') {
            input.classList.add('error');
        }
    };

    // Cleanup function to remove event listeners
    const removeEventListeners = () => {
        const cardNameInput = document.querySelector('[pc-sub="card-name"]') as HTMLInputElement;
        const cardNumberInput = document.querySelector('[pc-sub="card-number"]') as HTMLInputElement;
        const cardExpirationInput = document.querySelector('[pc-sub="card-expiration"]') as HTMLInputElement;
        const cardCvvInput = document.querySelector('[pc-sub="card-cvv"]') as HTMLInputElement;

        if (cardNameInput) {
            cardNameInput.removeEventListener('input', handleCardNameChange);
            cardNameInput.removeEventListener('blur', handleCardNameChange);
        }

        if (cardNumberInput) {
            cardNumberInput.removeEventListener('input', handleCardNumberChange);
            cardNumberInput.removeEventListener('blur', handleCardNumberChange);
        }

        if (cardExpirationInput) {
            cardExpirationInput.removeEventListener('input', handleCardExpirationChange);
            cardExpirationInput.removeEventListener('blur', handleCardExpirationChange);
        }

        if (cardCvvInput) {
            cardCvvInput.removeEventListener('input', handleCardCvvChange);
            cardCvvInput.removeEventListener('blur', handleCardCvvChange);
        }
    };


    const getSubscriptionData = async () => {
        await ApiRequest({
            url: "/subscriptions/getSubscriptionByCode",
            method: "GET",
            query: {
                subscription_code: subscriptionCode,
            },
            setResponse: async (response: any) => {
                if (response.status = "success") {
                    setSubscriptionData(response.data);
                    console.log('Subscription data loaded:', response.data);
                    

                    const subtotalElement = document.querySelector('[pc="summary-subtotal"]') as HTMLElement;
                    const deliveryElement = document.querySelector('[pc="summary-delivery"]') as HTMLElement;
                    const totalElement = document.querySelector('[pc="summary-total"]') as HTMLElement;
                    const taxElement = document.querySelector('[pc="summary-tax"]') as HTMLElement;
                    const discountElement = document.querySelector('[pc="summary-discount-value"]') as HTMLElement;

                    renderCartProducts(response.data.products)
                    
                    if (subtotalElement) {
                        subtotalElement.textContent = "$" + Number(response.data.subtotal || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    }
                    if (deliveryElement) {
                        deliveryElement.textContent = "$" + Number(response.data.shipping_price || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    }
                    if (totalElement) {
                        totalElement.textContent = "$" + Number(response.data.total_amount || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    }
                    if (taxElement) {
                        taxElement.textContent = "$" + Number(response.data.total_taxes || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    }
                    if (discountElement) {
                        discountElement.textContent = "$" + Number(0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    }
                } else {
                    enqueueSnackbar('Failed to load subscription data', { variant: 'error' });
                }
            },
        });
    }


    const updatePaymentDetails = async (event: any) => {
        event.preventDefault();
        
        // Get form values
        const cardName = (event.target.querySelector('[pc-sub="card-name"]') as HTMLInputElement)?.value;
        const cardNumber = (event.target.querySelector('[pc-sub="card-number"]') as HTMLInputElement)?.value;
        const cardExpiration = (event.target.querySelector('[pc-sub="card-expiration"]') as HTMLInputElement)?.value;
        const cardCvv = (event.target.querySelector('[pc-sub="card-cvv"]') as HTMLInputElement)?.value;
        
        // Validate all fields
        const isValidCardName = !!(cardName && cardName.trim().length >= 2);
        const isValidCardNumber = validateCardNumber(cardNumber || '');
        const isValidExpiration = validateExpirationDate(cardExpiration || '');
        const isValidCVV = !!(cardCvv && cardCvv.length >= 3);
        
        // Update validation state
        setValidationState({
            isValidCardName,
            isValidCardNumber,
            isValidExpiration,
            isValidCVV
        });
        
        // Add error classes to invalid fields
        const cardNameInput = event.target.querySelector('[pc-sub="card-name"]') as HTMLInputElement;
        const cardNumberInput = event.target.querySelector('[pc-sub="card-number"]') as HTMLInputElement;
        const cardExpirationInput = event.target.querySelector('[pc-sub="card-expiration"]') as HTMLInputElement;
        const cardCvvInput = event.target.querySelector('[pc-sub="card-cvv"]') as HTMLInputElement;
        
        if (cardNameInput) {
            if (isValidCardName) {
                cardNameInput.classList.remove('error');
            } else {
                cardNameInput.classList.add('error');
            }
        }
        
        if (cardNumberInput) {
            if (isValidCardNumber) {
                cardNumberInput.classList.remove('error');
            } else {
                cardNumberInput.classList.add('error');
            }
        }
        
        if (cardExpirationInput) {
            if (isValidExpiration) {
                cardExpirationInput.classList.remove('error');
            } else {
                cardExpirationInput.classList.add('error');
            }
        }
        
        if (cardCvvInput) {
            if (isValidCVV) {
                cardCvvInput.classList.remove('error');
            } else {
                cardCvvInput.classList.add('error');
            }
        }
        
        // Check if all validations pass
        if (!isValidCardName || !isValidCardNumber || !isValidExpiration || !isValidCVV) {
            enqueueSnackbar('Please check all payment details and try again', { variant: 'error' });
            return;
        }
        
        ShowLoader(true);
        
        const paymentData = {
            cardName: cardName?.trim(),
            cardNumber: cardNumber?.replace(/\s/g, ''), // Remove spaces for API
            cardExpiration,
            cardCvv,
            subscriptionCode
        };
        

        await ApiRequest({
            url: "/subscription/updatePaymentDetails",
            method: "POST",
            body: paymentData,
            setResponse: async (response: any) => {
                if (response.status === "success") {
                    enqueueSnackbar(response.message, { variant: 'success' });
                    setIsUpdateSuccessful(true);
                } else {
                    enqueueSnackbar(response.message, { variant: 'error' });
                }
            },
        });
        
        ShowLoader(false);
    }


    const handleReturnHome = () => {
        navigate("/", { replace: true });
    };

    return (
        <div className="sub-update-card">
            <div className="sub-update-card-popup">

                
                
                {!isUpdateSuccessful && (
                    
                    <form pc-sub="subscription-update-form" onSubmit={updatePaymentDetails}>
                        <h3>Update Payment Details</h3>
                    <div className="input_group" pc-sub="payment-form">
                        <div className="input_wrapper">
                            <label className="input_label">Name on Card</label>
                            <input 
                                type="text" 
                                pc-sub="card-name" 
                                placeholder="Name on Card" 
                                className="pc-sub-input"
                                maxLength={50}
                                required
                            />
                        </div>
                        
                        <div className="input_wrapper">
                            <label className="input_label">Card Number</label>
                            <input 
                                type="text" 
                                pc-sub="card-number" 
                                placeholder="1234 5678 9012 3456" 
                                className="pc-sub-input"
                                maxLength={19}
                                required
                            />
                        </div>
                        
                        <div className="input_row">
                            <div className="input_wrapper">
                                <label className="input_label">Expiration</label>
                                <input 
                                    type="text" 
                                    pc-sub="card-expiration" 
                                    placeholder="MM/YY" 
                                    className="pc-sub-input"
                                    maxLength={5}
                                    required
                                />
                            </div>
                            <div className="input_wrapper">
                                <label className="input_label">CVV</label>
                                <input 
                                    type="text" 
                                    pc-sub="card-cvv" 
                                    placeholder="123" 
                                    className="pc-sub-input"
                                    maxLength={4}
                                    required
                                />
                            </div>
                        </div>

                        <div className="pc-sub-buttons">
                            <button type="submit" className="pc-sub-button-full">Update Payment Details</button>
                        </div>
                    </div>
                </form>
                )}

                {isUpdateSuccessful && (
                    <div className="pc-sub-status">
                        <div className="pc-sub-status-message">
                            <h4>Your Subscription was successfully updated!</h4>
                            <p>The payment details for your subscription have been updated.</p>
                        </div>
                        <button onClick={handleReturnHome} className="pc-sub-button-full">Return to Home</button>
                    </div>
                )}

            </div>
        </div>
    );
}